import {Form, type FormSubmitHandler, showNotification} from 'platform/components';

import {useState} from 'react';
import {useDispatch} from 'react-redux';

import {isNil, isNotNil} from 'ramda';

import {
  checkoutProfilesApi,
  getCheckoutProfileErrors,
  type CheckoutProfile,
  useCreateCheckoutProfileMutation,
  useLazyGetCheckoutProfileQuery,
  useUpdateCheckoutProfileMutation,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {handleApiError, queryParams, refreshDmsDatagrid} from '@dms/shared';

import {type ApiError, composePath, useNavigate, useQueryState} from 'shared/web';

import {CHECKOUT_PROFILE_TAB_IDS} from '../../../constants/CHECKOUT_PROFILE_TAB_IDS';
import {CHECKOUT_PROFILE_VERSION_CONFLICT_CODES} from '../../../constants/CHECKOUT_PROFILE_VERSION_CONFLICT_CODES';
import type {CheckoutProfileServerResponse} from '../hooks/useCheckoutProfileFormState';
import {CheckoutProfileFormSchema} from '../types/CheckoutProfileFormSchema';
import type {CheckoutProfileFormValues} from '../types/CheckoutProfileFormValuesSchema';
import {getCheckoutProfileFeedbackSnapshot} from '../utils/getCheckoutProfileFeedbackSnapshot';
import {getCheckoutProfileFormValues} from '../utils/getCheckoutProfileFormValues';
import {CheckoutProfileFormContent} from './CheckoutProfileFormContent';

interface CheckoutProfileEditorProps {
  initialProfile?: CheckoutProfile;
  branchId?: string;
  currency?: string;
}

export function CheckoutProfileEditor(props: CheckoutProfileEditorProps) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [tabId] = useQueryState(queryParams.COMPONENT_SECTIONS_TAB);
  const [profile, setProfile] = useState(props.initialProfile);
  // Remounts the form with the stored profile as its new default values.
  const [reloadCount, setReloadCount] = useState(0);
  const [createProfile] = useCreateCheckoutProfileMutation();
  const [updateProfile] = useUpdateCheckoutProfileMutation();
  const [loadProfile] = useLazyGetCheckoutProfileQuery();
  const [serverResponse, setServerResponse] = useState<CheckoutProfileServerResponse>();

  const handleReload = () => {
    if (isNil(profile)) {
      return;
    }

    loadProfile({checkoutProfileId: profile.id}, false)
      .unwrap()
      .then((stored) => {
        setProfile(stored);
        setServerResponse(undefined);
        setReloadCount((count) => count + 1);
      })
      .catch(handleApiError);
  };

  const onSubmit: FormSubmitHandler<CheckoutProfileFormValues> = async (
    values,
    _setFieldErrors,
    reset,
    blocker
  ) => {
    setServerResponse(undefined);
    const request = isNotNil(profile)
      ? updateProfile({checkoutProfileId: profile.id, body: {...values, version: profile.version}})
      : createProfile({body: values});

    await request
      .unwrap()
      .then((saved) => {
        setProfile(saved);
        reset(getCheckoutProfileFormValues(saved));
        showNotification.success(i18n.t('general.notifications.changesSaved'));
        refreshDmsDatagrid('checkout-profile');
        refreshDmsDatagrid('checkout-rule');
        refreshDmsDatagrid('checkout-profile-rule');

        if (!blocker?.wasSubmittedFromBlocker && isNil(profile)) {
          navigate(
            composePath(settingsRoutes.checkoutProfileDetail, {
              params: {id: saved.id},
              isSearchParamsPreserved: false,
            })
          );
        }
      })
      .catch((error: ApiError) => {
        blocker?.reportApiFailure?.();
        const refusal = getCheckoutProfileErrors(error);

        if (refusal) {
          const versionRefused = refusal.errors.some((issue) =>
            CHECKOUT_PROFILE_VERSION_CONFLICT_CODES.some((code) => code === issue.code)
          );

          // The stored version is newer than the one held here, so subscribers reload it.
          if (versionRefused && isNotNil(profile)) {
            dispatch(
              checkoutProfilesApi.util.invalidateTags([{type: 'CheckoutProfile', id: profile.id}])
            );
          }

          const activationRefused =
            values.isActive &&
            refusal.errors.some(
              (issue) => issue.code === 'PROFILE_INCOMPLETE' && issue.path === 'isActive'
            );

          if (activationRefused) {
            reset((current) => ({...current, isActive: false}), {
              keepDefaultValues: true,
              keepErrors: true,
              keepTouched: true,
            });
          }

          const submittedValues = activationRefused ? {...values, isActive: false} : values;

          setServerResponse({
            errors: refusal.errors.map((issue) =>
              activationRefused && issue.code === 'PROFILE_INCOMPLETE'
                ? {
                    ...issue,
                    message: i18n.t('entity.checkout.validation.activationIncomplete'),
                  }
                : issue
            ),
            values: submittedValues,
            snapshot: getCheckoutProfileFeedbackSnapshot(submittedValues),
            tab: tabId ?? CHECKOUT_PROFILE_TAB_IDS.general,
          });
        } else {
          handleApiError(error);
        }
      });
  };

  return (
    <Form<CheckoutProfileFormValues>
      key={reloadCount}
      experimentalZodSchema={CheckoutProfileFormSchema}
      mode="onChange"
      defaultValues={getCheckoutProfileFormValues(profile, props.branchId ?? '')}
      onSubmit={onSubmit}
      isFullHeight
      shouldWatchForUnsavedChanges
      data-testid="checkout-profile-form"
    >
      {(control, formApi, navigateWithoutBlocker, withoutBlocker) => (
        <CheckoutProfileFormContent
          control={control}
          formApi={formApi}
          profile={profile}
          currency={props.currency}
          navigateWithoutBlocker={navigateWithoutBlocker}
          withoutBlocker={withoutBlocker}
          serverResponse={serverResponse}
          onReload={handleReload}
        />
      )}
    </Form>
  );
}
